<template>
  <el-dialog
    :model-value="modelValue"
    modal-class="feedback-dialog-shell"
    width="min(92vw, 520px)"
    :close-on-click-modal="!submitting"
    :close-on-press-escape="!submitting"
    @update:model-value="handleVisibleChange"
  >
    <template #header>
      <div class="feedback-dialog-title">
        <span>Feedback Box</span>
        <strong>意见箱</strong>
      </div>
    </template>

    <div class="feedback-dialog-body">
      <p>如果您有关于后室宝贵的意见，欢迎投稿</p>
      <el-input
        v-model="text"
        type="textarea"
        maxlength="2000"
        show-word-limit
        :autosize="{ minRows: 6, maxRows: 10 }"
        :disabled="submitting"
        placeholder="写下你的建议、想法，或发现的问题..."
        @keydown.ctrl.enter="submitFeedback"
      />
    </div>

    <template #footer>
      <div class="feedback-dialog-actions">
        <el-button class="feedback-cancel-btn" :disabled="submitting" @click="closeDialog">取消</el-button>
        <el-button class="feedback-submit-btn" :loading="submitting" :disabled="!canSubmit" @click="submitFeedback">
          提交投稿
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { ajax } from '@/api';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const text = ref('');
const submitting = ref(false);
const canSubmit = computed(() => text.value.trim().length > 0 && !submitting.value);

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      text.value = '';
    }
  }
);

const handleVisibleChange = (visible: boolean) => {
  if (!visible && submitting.value) return;
  emit('update:modelValue', visible);
};

const closeDialog = () => {
  handleVisibleChange(false);
};

const submitFeedback = () => {
  const content = text.value.trim();
  if (!content) {
    ElMessage.warning('请先输入投稿内容');
    return;
  }

  submitting.value = true;
  ajax({
    url: 'feedback/submit',
    method: 'POST',
    data: { text: content },
    isLoading: false,
    success: (res: any) => {
      submitting.value = false;
      if (res.code === 0) {
        ElMessage.success(res.msg || '投稿成功，感谢你的意见');
        text.value = '';
        closeDialog();
      } else {
        ElMessage.error(res.msg || '投稿失败，请稍后重试');
      }
    },
    error: () => {
      submitting.value = false;
      ElMessage.error('投稿失败，请稍后重试');
    }
  });
};
</script>

<style lang="scss">
.feedback-dialog-shell {
  background: rgba(2, 6, 18, 0.78) !important;
  backdrop-filter: blur(8px);
}

.feedback-dialog-shell .el-dialog {
  overflow: hidden;
  border: 1px solid rgba(0, 240, 216, 0.35);
  border-radius: 12px;
  background:
    radial-gradient(circle at 16% 0%, rgba(34, 184, 255, 0.16), transparent 34%),
    linear-gradient(145deg, rgba(0, 240, 216, 0.08), rgba(157, 92, 255, 0.045)),
    #080d1a;
  box-shadow: 0 30px 120px rgba(0, 0, 0, 0.72), 0 0 48px rgba(0, 240, 216, 0.13);
}

.feedback-dialog-shell .el-dialog__header {
  padding: 24px 26px 8px;
  margin: 0;
}

.feedback-dialog-shell .el-dialog__body {
  padding: 12px 26px 0;
}

.feedback-dialog-shell .el-dialog__footer {
  padding: 20px 26px 26px;
}

.feedback-dialog-shell .el-dialog__headerbtn .el-dialog__close {
  color: rgba(232, 237, 247, 0.72);
}

.feedback-dialog-shell .el-dialog__headerbtn:hover .el-dialog__close {
  color: #00f0d8;
}

.feedback-dialog-title {
  display: grid;
  gap: 6px;
}

.feedback-dialog-title span {
  color: #22b8ff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.feedback-dialog-title strong {
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
}

.feedback-dialog-body {
  display: grid;
  gap: 14px;
}

.feedback-dialog-body p {
  color: #f8fafc;
  line-height: 1.8;
}

.feedback-dialog-body small {
  color: #94a3b8;
  line-height: 1.6;
}

.feedback-dialog-shell .el-textarea__inner {
  color: #f8fafc;
  border: 1px solid rgba(0, 240, 216, 0.36);
  border-radius: 10px;
  background: rgba(5, 10, 22, 0.82);
  box-shadow: inset 0 0 0 1px rgba(0, 240, 216, 0.08), 0 0 24px rgba(0, 240, 216, 0.08);
  caret-color: #00f0d8;
  line-height: 1.7;
}

.feedback-dialog-shell .el-textarea__inner:focus {
  border-color: rgba(0, 240, 216, 0.72);
  box-shadow: inset 0 0 0 1px rgba(0, 240, 216, 0.2), 0 0 30px rgba(0, 240, 216, 0.12);
}

.feedback-dialog-shell .el-textarea__inner::placeholder {
  color: rgba(148, 163, 184, 0.72);
}

.feedback-dialog-shell .el-textarea .el-input__count,
.feedback-dialog-shell .el-input__count,
.feedback-dialog-shell .el-input__count .el-input__count-inner {
  color: #94a3b8;
  background: transparent !important;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.75);
}

.feedback-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.feedback-dialog-actions .el-button {
  min-height: 38px;
  margin-left: 0;
  border-radius: 8px;
  font-weight: 800;
}

.feedback-dialog-actions .feedback-cancel-btn {
  --el-button-bg-color: rgba(8, 13, 26, 0.86);
  --el-button-border-color: rgba(55, 65, 81, 0.9);
  --el-button-text-color: #94a3b8;
  --el-button-hover-bg-color: rgba(0, 240, 216, 0.1);
  --el-button-hover-border-color: rgba(0, 240, 216, 0.58);
  --el-button-hover-text-color: #00f0d8;
  color: #94a3b8 !important;
  border-color: rgba(55, 65, 81, 0.9) !important;
  background: rgba(8, 13, 26, 0.86) !important;
}

.feedback-dialog-actions .feedback-cancel-btn:hover {
  color: #00f0d8 !important;
  border-color: rgba(0, 240, 216, 0.58) !important;
  background: rgba(0, 240, 216, 0.1) !important;
}

.feedback-dialog-actions .feedback-submit-btn {
  --el-button-bg-color: rgba(0, 240, 216, 0.96);
  --el-button-border-color: rgba(0, 240, 216, 0.78);
  --el-button-text-color: #03101a;
  --el-button-hover-bg-color: #a6fff4;
  --el-button-hover-border-color: rgba(166, 255, 244, 0.96);
  --el-button-hover-text-color: #03101a;
  color: #03101a !important;
  border-color: rgba(0, 240, 216, 0.78) !important;
  background: linear-gradient(135deg, rgba(0, 240, 216, 0.96), rgba(34, 184, 255, 0.9)) !important;
  box-shadow: 0 0 26px rgba(0, 240, 216, 0.22) !important;
}

.feedback-dialog-actions .feedback-submit-btn:hover {
  color: #03101a !important;
  border-color: rgba(166, 255, 244, 0.96) !important;
  background: linear-gradient(135deg, #a6fff4, #75dcff) !important;
}
</style>
